// Binds the peer-cursors overlay to the real per-project presence channel
// (see docs/game-maker-completion/design.md section 8): cursor position and
// active selection over the API's `/ws`, riding the same LISTEN/NOTIFY fan-out
// as map_saved/doc_saved/project_renamed. Presence plus last-write-wins on
// document saves is the target; this hook does not attempt simultaneous
// co-editing or conflict resolution, and the wire policy itself lives in the
// framework-free peerPresence.ts so it is unit-testable without a socket.
import { useEffect, useRef, useState } from 'react';

import { useMapEditorStore } from '../../engine';
import { useAuth } from '../auth/AuthProvider';
import { API_URL } from '../services/api';
import { getIdToken } from '../services/auth';
import {
  mergePresence,
  presenceSocketUrl,
  pruneStale,
  resolvePresenceWsBase,
  shouldSendPresence,
  type PeerCursor,
  type PeerPresenceEvent,
  type PresenceSendState,
} from './peerPresence';

export type { PeerCursor } from './peerPresence';

const SAMPLE_INTERVAL_MS = 150; // how often the local pointer/selection is sampled for an outgoing update
const HEARTBEAT_MS = 5000; // resend even without change, so a stationary peer is not pruned as stale
const STALE_TIMEOUT_MS = 15000; // drop a peer not heard from (closed tab, dropped socket, no explicit "gone")
const RECONNECT_DELAY_MS = 3000;
const PING_INTERVAL_MS = 25000; // keeps the socket alive through idle proxy timeouts

function randomClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `peer-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

export function usePeerCursors(projectId: string) {
  const { user } = useAuth();
  const selection = useMapEditorStore((state) => state.selectedObjectIds);
  const [peers, setPeers] = useState<PeerCursor[]>([]);

  const clientIdRef = useRef<string | null>(null);
  if (clientIdRef.current === null) clientIdRef.current = randomClientId();

  const cursorRef = useRef<[number, number, number] | null>(null);
  const selectionRef = useRef<string[]>(selection);
  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      cursorRef.current = [e.clientX, e.clientY, 0];
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    if (!projectId || !user) return;
    const clientId = clientIdRef.current as string;
    const topic = `project:${projectId}`;
    const lastSeen = new Map<string, number>();
    let cancelled = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let sampleTimer: ReturnType<typeof setInterval> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let lastSent: PresenceSendState | null = null;

    const stopTimers = () => {
      if (sampleTimer !== null) clearInterval(sampleTimer);
      if (pingTimer !== null) clearInterval(pingTimer);
      sampleTimer = null;
      pingTimer = null;
    };

    const connect = async () => {
      const token = await getIdToken();
      if (cancelled) return;
      const wsBase = resolvePresenceWsBase(API_URL, import.meta.env.VITE_WS_URL);
      const ws = new WebSocket(presenceSocketUrl(wsBase, token ?? ''));
      socket = ws;

      ws.addEventListener('open', () => {
        ws.send(JSON.stringify({ op: 'subscribe', topic }));

        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ op: 'ping' }));
        }, PING_INTERVAL_MS);

        sampleTimer = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const next = { cursor: cursorRef.current, selection: selectionRef.current };
          const now = Date.now();
          if (!shouldSendPresence(lastSent, next, now, HEARTBEAT_MS)) return;
          ws.send(JSON.stringify({ op: 'presence', topic, clientId, ...next }));
          lastSent = { ...next, sentAt: now };
        }, SAMPLE_INTERVAL_MS);
      });

      ws.addEventListener('message', (event) => {
        let msg: { type?: string; data?: PeerPresenceEvent };
        try {
          msg = JSON.parse(event.data as string);
        } catch {
          return;
        }
        if (msg.type !== 'peer_presence' || !msg.data) return;
        const presence = msg.data;
        if (presence.clientId === clientId) return; // this browser's own echo
        lastSeen.set(presence.clientId, Date.now());
        setPeers((prev) => mergePresence(prev, presence));
      });

      ws.addEventListener('close', () => {
        stopTimers();
        if (!cancelled) reconnectTimer = setTimeout(() => void connect(), RECONNECT_DELAY_MS);
      });
    };

    void connect();

    const pruneTimer = setInterval(() => {
      setPeers((prev) => pruneStale(prev, lastSeen, Date.now(), STALE_TIMEOUT_MS));
    }, STALE_TIMEOUT_MS / 3);

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      clearInterval(pruneTimer);
      stopTimers();
      socket?.close();
      setPeers([]);
    };
  }, [projectId, user]);

  return peers;
}
